import { Graphics, type Sprite } from 'pixi.js';
import { DOME_SCALE as SCALE } from '../../data/full/dome';
import { FULL_SUMMONS } from '../../data/full/summons';
import type { SummonDefinition } from '../../data/types';
import { lerp } from '../../shared/scalar';
import { HEAVY_HALF_LENGTH, summonOf } from '../../sim/full/summons';
import type { SimState } from '../../sim/state';
import type { Family, RenderContext } from '../context';
import type { Frame } from '../frame';
import { add, hide, setTint } from '../util';
import { GIANT_TOKENS } from './giants';
import { Pen } from './pen';

const TAU = Math.PI * 2;
const CENTER = { x: 800, y: 500 };
const DIAGONALS = [-0.75, -0.25, 0.25, 0.75].map((turn) => turn * Math.PI);
const CREAM = 0xfffaeb;
const INK = 0x140a18;
const AURA = { x: 488, radius: 76, dots: 48 } as const;
const CAP_RADIUS = 30;
const ORB_RADIUS = 7;

export function createGiantsWake(ctx: RenderContext): Family {
  const { layers, textures: t } = ctx;
  const caps = new Graphics();
  layers.glow.addChild(caps);
  const capPen = new Pen(caps);
  const patches = new Graphics();
  layers.traps.addChild(patches);
  const carried = new Graphics();
  layers.weapons.addChild(carried);
  const halos: Sprite[] = DIAGONALS.map(() => add(layers.glow, t.halo));

  function clear(): void {
    capPen.clear();
    patches.clear();
    carried.clear();
    hide(...halos);
  }

  return {
    update(state: SimState, alpha: number, frame: Frame): void {
      const definition = giantsOf(state);
      clear();
      if (definition === undefined) {
        return;
      }
      const summon = summonOf(state);
      const pulse = frame.calm ? 0.4 : frame.pulse;
      const spin = frame.calm ? 0 : frame.now / frame.tempo.ticksPerBeat / 40;

      DIAGONALS.forEach((angle, index) => {
        const halo = halos[index];
        const token = GIANT_TOKENS[index];
        if ((summon?.burnt[index] ?? 0) === 0 || halo === undefined || token === undefined) {
          return;
        }
        const color = frame.palette[token];
        capPen.save().translate(CENTER.x, CENTER.y).rotate(angle).scale(SCALE);
        capPath(caps);
        caps.fill({ color, alpha: 0.6 + 0.25 * pulse });
        caps.stroke({ width: 3, color: CREAM, alpha: 0.8 });
        for (const [x, y, r] of [
          [514, -28, 5],
          [520, 20, 4],
          [490, -6, 3.5],
        ] as const) {
          caps.circle(x, y, r * (1 + 0.2 * pulse)).fill({ color: CREAM, alpha: 0.95 });
        }
        for (let dot = 0; dot < AURA.dots; dot++) {
          const turn = (dot / AURA.dots) * TAU + spin * TAU;
          caps
            .circle(AURA.x + Math.cos(turn) * AURA.radius, Math.sin(turn) * AURA.radius, 1.8)
            .fill({ color, alpha: 0.9 });
        }
        capPen.restore();
        const reach = 500 * SCALE;
        halo.visible = true;
        setTint(halo, color);
        halo.position.set(CENTER.x + Math.cos(angle) * reach, CENTER.y + Math.sin(angle) * reach);
        halo.scale.set((110 * SCALE) / t.halo.radius);
        halo.alpha = (0.45 + 0.25 * pulse) * frame.light.haloAlpha;
      });

      definition.sources.forEach((source, index) => {
        if (source.items - (summon?.taken[index] ?? 0) <= 0) {
          return;
        }
        const dashes = 16;
        for (let dash = 0; dash < dashes; dash++) {
          const from = (dash * TAU) / dashes + spin * TAU;
          const to = from + (TAU / dashes) * 0.5;
          const radius = source.radius + 14;
          patches
            .moveTo(source.x + Math.cos(from) * radius, source.y + Math.sin(from) * radius)
            .arc(source.x, source.y, radius, from, to)
            .stroke({ width: 2, color: frame.palette.or, alpha: 0.35 + 0.4 * pulse });
        }
      });

      for (const heavy of summon?.heavy ?? []) {
        const x = lerp(heavy.prevX, heavy.x, alpha);
        const y = lerp(heavy.prevY, heavy.y, alpha);
        drawMushroomCap(carried, x, y, frame.palette.mage, pulse);
      }
      for (const player of state.players) {
        if (player.offering === undefined) {
          continue;
        }
        const angle = Math.atan2(player.aim.y, player.aim.x) + 0.6;
        const reach = player.radius + ORB_RADIUS + 2;
        const x = lerp(player.prevX, player.x, alpha) + Math.cos(angle) * reach;
        const y = lerp(player.prevY, player.y, alpha) + Math.sin(angle) * reach;
        carried
          .circle(x, y, ORB_RADIUS + 5 * pulse)
          .fill({ color: frame.palette.mage, alpha: 0.25 });
        carried
          .circle(x, y, ORB_RADIUS)
          .fill({ color: 0xffc8e6 })
          .stroke({ width: 2.4, color: INK });
        for (let spore = 0; spore < 8; spore++) {
          const turn = (spore / 8) * TAU + spin * TAU * 8;
          carried
            .circle(
              x + Math.cos(turn) * (ORB_RADIUS + 5),
              y + Math.sin(turn) * (ORB_RADIUS + 5),
              1.3,
            )
            .fill({ color: CREAM });
        }
      }
    },
    destroy(): void {
      caps.destroy();
      patches.destroy();
      carried.destroy();
      for (const halo of halos) {
        halo.destroy();
      }
    },
  };
}

function giantsOf(state: SimState): SummonDefinition | undefined {
  return FULL_SUMMONS.find((summon) => summon.setId === state.setId && summon.look === 'giants');
}

function capPath(g: Graphics): void {
  g.moveTo(462, -64)
    .bezierCurveTo(510, -66, 545, -36, 545, 0)
    .bezierCurveTo(545, 36, 510, 66, 462, 64)
    .bezierCurveTo(454, 30, 454, -30, 462, -64)
    .closePath();
}

function drawMushroomCap(g: Graphics, x: number, y: number, color: number, pulse: number): void {
  const radius = Math.min(CAP_RADIUS, HEAVY_HALF_LENGTH - 8);
  g.circle(x, y, radius * 1.6).fill({ color, alpha: 0.15 + 0.1 * pulse });
  g.circle(x, y, radius).fill({ color }).stroke({ width: 3, color: INK });
  g.circle(x, y, radius * 0.55).stroke({ width: 1.2, color: CREAM, alpha: 0.5 });
  for (const [dx, dy, size] of [
    [0.35, -0.3, 0.2],
    [-0.4, 0.1, 0.16],
    [0.1, 0.45, 0.14],
    [-0.15, -0.5, 0.12],
  ] as const) {
    g.circle(x + dx * radius, y + dy * radius, size * radius).fill({ color: CREAM, alpha: 0.9 });
  }
}
